import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Analyst / "Workbench" data layer: the thesis dossier (centrepiece), falsify (steelman),
// scenario causal trace, and the two divergence lenses. A thesis is reached by an internal
// edge id obtained from a list RPC — never typed by the user.

type Row = Record<string, any>;

export interface ThesisDossier extends Row {
  thesis?: Row;
  for_evidence?: Row[];
  against_evidence?: Row[];
  evolution?: Row[];
  source_calibration?: Row[];
  provenance?: Row;
  run_meta?: Row;
  error?: string; // 'not_a_thesis'
}
export function useAnalystDossier(edgeId: string | undefined) {
  return useQuery({
    queryKey: ["research_analyst_thesis_dossier", edgeId],
    enabled: !!edgeId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_analyst_thesis_dossier", {
        _thesis_edge_id: edgeId as string,
        _limit: 20,
      });
      if (error) throw error;
      return (data ?? {}) as unknown as ThesisDossier;
    },
  });
}

export interface FalsifyRow extends Row {
  thesis_edge_id?: string;
  counter_edge_id?: string;
  counter_direction?: string;
  disagreement_kind?: string;
  evidence_quote?: string;
  strength?: number;
  conflict_id?: string;
  source_count?: number;
  occurred_at?: string;
}
export function useFalsify(edgeId: string | undefined, limit = 25) {
  return useQuery({
    queryKey: ["research_kg_falsify", edgeId, limit],
    enabled: !!edgeId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_kg_falsify", {
        _thesis_edge_id: edgeId as string,
        _limit: limit,
      });
      if (error) throw error;
      return ((data as unknown as FalsifyRow[]) ?? []).filter(Boolean);
    },
  });
}

export interface ScenarioTrace extends Row {
  scenario?: { label?: string } & Row;
  target?: { label?: string } & Row;
  reachable?: boolean;
  paths?: Row[];
  ambiguity?: Row[];
  error?: string; // 'scenario_not_resolved' | 'target_not_resolved'
}
export function useScenarioTrace(
  params: { scenario: string; target?: string; maxHops?: number; minScore?: number } | null,
) {
  return useQuery({
    queryKey: ["research_analyst_scenario_trace", params],
    enabled: !!params?.scenario,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const args: Row = {
        _scenario: params!.scenario,
        _max_hops: params?.maxHops ?? 4,
        _min_score: params?.minScore ?? 0.0,
      };
      if (params?.target) args._target = params.target;
      const { data, error } = await supabase.rpc("research_analyst_scenario_trace", args as any);
      if (error) throw error;
      return (data ?? {}) as unknown as ScenarioTrace;
    },
  });
}

// Mirrors the real `research_thesis_health_scan` return columns (see types.ts). Note there is NO
// `no_signal_coverage` boolean — "no live signal" is the `health_status` value `no_signal_coverage`.
export interface ThesisHealthScanRow extends Row {
  claim_edge_id?: string;
  signal_key?: string;
  signal_label?: string;
  thesis_subject?: string;
  thesis_direction?: string;
  conviction?: number;
  health_status?: string;
  severity?: string;
  summary?: string;
  market_direction?: number;
  market_value?: number;
  evidence_source_ids?: string[];
}
export function useThesisHealthScan() {
  return useQuery({
    queryKey: ["research_thesis_health_scan"],
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("research_thesis_health_scan", {});
      if (error) throw error;
      return ((data as unknown as ThesisHealthScanRow[]) ?? []).filter(Boolean);
    },
  });
}

export interface ManagerStanceRow extends Row {
  subject_label?: string;
  our_direction?: string;
  cohort?: string;
  manager_label?: string;
  manager_direction?: string;
  n_managers?: number;
  n_agree?: number;
  n_disagree?: number;
}

// Multi-cohort / manager-consistency divergence ("where managers disagree").
// As of this build there is NO browser-reachable endpoint for it (no RPC/view/table on prod),
// pending real Street-consensus / T3 sell-side feeds. We call the INTENDED RPC and treat a
// missing-function / not-granted error as "not available yet" (available:false) so the screen
// shows a calm awaiting-data state and lights up automatically once the feed + RPC land.
export interface ManagerConsistencyResult {
  available: boolean;
  rows: ManagerStanceRow[];
}
export function useManagerConsistency() {
  return useQuery<ManagerConsistencyResult>({
    queryKey: ["research_manager_consistency"],
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      try {
        const { data, error } = await (supabase as any).rpc("research_manager_consistency", {});
        if (error) return { available: false, rows: [] };
        return { available: true, rows: ((data as ManagerStanceRow[]) ?? []).filter(Boolean) };
      } catch {
        return { available: false, rows: [] };
      }
    },
  });
}
